import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, ArrowRight, Loader2, CheckCircle2,
  ChevronRight, Sparkles, MailCheck, ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  clearRetiredInviteNotice,
  removeRetiredInviteFromUrl,
  retiredInviteNoticeRequired,
  showNormalSignInFromRetiredInvite,
} from "@/lib/legacyInviteUrl";
import { isNativePlatform } from "@/lib/platform";
// Brand Package Adoption Phase 1 · single source of truth for canonical
// brand asset URLs (see /app/docs/brand/PHASE-1-ADOPTION-AUDIT.md).
import { BRAND_LOGO, BRAND_META } from "@/lib/BrandAssets";
import {
  generateNonce,
  signInWithApple,
  appleDisplayName,
  initiateMicrosoftRedirect,
  describeSsoBackendError,
  nativeSessionNotEstablishedError,
  FederatedAuthError,
} from "@/lib/federatedAuth";
import { launchNativeGoogleBroker } from "@/lib/mobile/nativeGoogleBroker";

// Login page — federated identity providers (open registration).
// -----------------------------------------------------------------
// Providers:
//   • Google (Emergent-managed OAuth · PD-028) — full-page redirect
//   • Apple (Sign in with Apple JS SDK) — popup, id_token flow
//   • Microsoft (MSAL for Entra ID) — full-page redirect (loginRedirect)
//
// Endpoints used:
//   • Open-registration signin  → POST /api/auth/sso/{provider}/signin
//                                  (first-time signup and returning
//                                   sign-in — see routes_sso_signin.py)
//   • Google session exchange   → POST /api/auth/google/session (PD-028)
//   • Legacy magic-link URLs render a non-authenticating retirement notice.
//
// Screen modes (dispatched by <AnimatePresence>):
//   login | verify-pending | verify-consuming | verify-success | verify-error
//        | invite-retired
//
// The BrandCanvas (ship-at-sea artwork, wordmark, tagline) is FROZEN.

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=1600&q=80";

// Module-scoped guard — React 18 StrictMode double-mount safe.
const _consumedTokens = new Set();

// M3 · Post-login hash preservation for Family Sharing invitation review.
// Google SSO does a full-page redirect through Emergent's broker, and
// the return leg lands with `#session_id=...` — meaning the original
// hash (e.g. `#family-sharing/review/inv_xxxx`) is lost. Apple / Microsoft
// popup flows keep the hash on the parent page, so this only strictly
// matters for Google, but we stash on every unauthenticated visit so
// the behaviour is uniform. Restored by <AuthCallback> after the
// server-side session is established. Allow-list is deliberately narrow
// to avoid arbitrary hash injection.
const _POST_LOGIN_HASH_KEY = "cc_post_login_hash";
const _POST_LOGIN_ALLOWED_PREFIXES = [
  "family-sharing/review/",
  "shared-with-me",
  "shared/",
];
function _capturePostLoginHash() {
  try {
    const raw = window.location.hash || "";
    const slug = raw.replace(/^#/, "").split("?")[0];
    if (!slug) return;
    const ok = _POST_LOGIN_ALLOWED_PREFIXES.some(
      (p) => slug === p || slug.startsWith(p),
    );
    if (ok) sessionStorage.setItem(_POST_LOGIN_HASH_KEY, `#${slug}`);
  } catch { /* sessionStorage unavailable — silently ignore */ }
}

// -----------------------------------------------------------------
export default function LoginPage() {
  const { refreshMe } = useAuth();
  const [mode, setMode] = useState(() => retiredInviteNoticeRequired() ? "invite-retired" : "login");
  const [verifyError, setVerifyError] = useState(null);

  // Router-style URL handoff on first mount.
  useEffect(() => {
    _capturePostLoginHash();
    const url = new URL(window.location.href);
    const verifyToken = url.searchParams.get("verify");
    const inviteToken = retiredInviteNoticeRequired();

    if (inviteToken) {
      setMode("invite-retired");
      removeRetiredInviteFromUrl();
      return;
    }

    if (!verifyToken) return;
    if (_consumedTokens.has(verifyToken)) return;
    _consumedTokens.add(verifyToken);
    setMode("verify-consuming");
    (async () => {
      try {
        await api.post("/api/auth/verify", { token: verifyToken });
        setMode("verify-success");
      } catch (err) {
        setVerifyError(formatApiError(err));
        setMode("verify-error");
      } finally {
        url.searchParams.delete("verify");
        window.history.replaceState({}, "", url.toString());
      }
    })();
  }, []);

  useEffect(() => {
    const handleLegacyInviteNavigation = () => {
      if (!retiredInviteNoticeRequired()) return;
      setMode("invite-retired");
      removeRetiredInviteFromUrl();
    };
    window.addEventListener("hashchange", handleLegacyInviteNavigation);
    window.addEventListener("popstate", handleLegacyInviteNavigation);
    return () => {
      window.removeEventListener("hashchange", handleLegacyInviteNavigation);
      window.removeEventListener("popstate", handleLegacyInviteNavigation);
    };
  }, []);

  const onSignedIn = useCallback(async () => {
    clearRetiredInviteNotice();
    // null ⇒ /api/auth/me did not hydrate; native Apple callers surface that.
    return refreshMe();
  }, [refreshMe]);

  return (
    <div
      className="min-h-screen w-full bg-[#0F172A] text-white grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]"
      data-testid="cc-login-root"
    >
      <BrandCanvas />
      <div className="relative flex items-center justify-center p-8 lg:p-14 xl:p-16">
        <div className="w-full max-w-[440px]">
          <div className="lg:hidden mb-12 self-start" data-testid="cc-brand-lockup-mobile">
            <img
              src={BRAND_LOGO.reverse}
              alt={BRAND_META.productName}
              className="h-16 sm:h-20 w-auto select-none drop-shadow-[0_2px_14px_rgba(0,0,0,0.35)]"
              draggable={false}
            />
            <div className="mt-3 flex items-center gap-2 pl-0.5">
              <span className="h-[2px] w-8 rounded-full bg-gradient-to-r from-[#F5B400] to-[#F5B400]/20" />
              <span className="text-[10.5px] font-black tracking-[0.3em] uppercase text-white/80">
                Voyage Intelligence
              </span>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {mode === "login" && (
              <motion.div key="login" {...panelMotion} data-testid="cc-auth-login">
                <SignInCard onSignedIn={onSignedIn} />
              </motion.div>
            )}
            {mode === "verify-pending" && (
              <motion.div key="verify-pending" {...panelMotion} data-testid="cc-auth-verify-pending">
                <VerifyPending onBackToLogin={() => setMode("login")} />
              </motion.div>
            )}
            {mode === "verify-consuming" && (
              <motion.div key="verify-consuming" {...panelMotion}>
                <VerifyConsuming />
              </motion.div>
            )}
            {mode === "verify-success" && (
              <motion.div key="verify-success" {...panelMotion} data-testid="cc-auth-verify-success">
                <VerifySuccess onContinue={() => setMode("login")} />
              </motion.div>
            )}
            {mode === "verify-error" && (
              <motion.div key="verify-error" {...panelMotion} data-testid="cc-auth-verify-error">
                <VerifyError message={verifyError} onBack={() => setMode("login")} />
              </motion.div>
            )}
            {mode === "invite-retired" && (
              <motion.div key="invite-retired" {...panelMotion} data-testid="cc-auth-invite-retired">
                <InviteRetired onBack={() => { showNormalSignInFromRetiredInvite(); setMode("login"); }} />
              </motion.div>
            )}
          </AnimatePresence>

          <div
            data-testid="cc-auth-legal-footer"
            className="mt-10 pt-5 border-t border-white/10 flex items-center justify-center gap-4 text-[11px] tracking-wide text-white/40"
          >
            <a href="/legal/privacy.html" data-testid="cc-auth-legal-privacy" className="hover:text-white/70 transition-colors">Privacy Policy</a>
            <span className="text-white/20">·</span>
            <a href="/legal/terms.html" data-testid="cc-auth-legal-terms" className="hover:text-white/70 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </div>
  );
}

const panelMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -8 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

// -----------------------------------------------------------------
// BrandCanvas · ship-at-sea half of the split screen (FROZEN artwork)
// -----------------------------------------------------------------
function BrandCanvas() {
  return (
    <div className="relative hidden lg:block overflow-hidden">
      <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{
        background: "linear-gradient(135deg, rgba(9,25,54,0.85) 0%, rgba(30,109,229,0.55) 55%, rgba(9,25,54,0.20) 100%)",
      }} />
      <div className="relative z-10 h-full flex flex-col justify-between items-start p-12 lg:p-14 xl:p-16">
        <div className="inline-flex flex-col gap-3 self-start">
          <img
            src={BRAND_LOGO.reverse}
            alt={BRAND_META.productName}
            data-testid="cc-brand-lockup"
            className="h-20 sm:h-24 lg:h-[88px] xl:h-[96px] w-auto select-none drop-shadow-[0_4px_22px_rgba(0,0,0,0.4)]"
            draggable={false}
          />
          <div className="flex items-center gap-2.5 pl-1">
            <span className="h-[2px] w-10 rounded-full bg-gradient-to-r from-[#F5B400] to-[#F5B400]/20" />
            <span className="text-[11px] font-black tracking-[0.32em] uppercase text-white/90">Voyage Intelligence</span>
          </div>
        </div>
        <div className="max-w-md">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/12 border border-white/25 px-3 py-1 mb-4">
            <Sparkles className="w-3 h-3" />
            <span className="text-[10.5px] font-black tracking-[0.22em] uppercase">Plan every voyage, once.</span>
          </div>
          <h1 className="font-display font-bold text-white text-[42px] leading-[1.05] tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.4)]">
            Your cruise, thoughtfully curated.
          </h1>
          <p className="mt-4 text-[15px] font-medium text-white/85 leading-relaxed">
            From your first reservation to your journey home, Cruise Command keeps every detail beautifully organized.
          </p>
        </div>
        <div className="text-[11px] text-white/60 font-medium" data-testid="cc-auth-version-copy">CRUISE COMMAND · VERSION 1.0 · 2026</div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SignInCard · Welcome · federated identity providers (open registration).
// -----------------------------------------------------------------
function SignInCard({ onSignedIn }) {
  const [busy, setBusy] = useState(null);        // "google" | "apple" | "microsoft" | null
  const [error, setError] = useState(null);      // { title, message, code }

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isNativePlatform()) {
      // The broker validates `state` against a cookie it sets in the browser
      // that started the attempt (5-minute window). Keep the whole round-trip
      // in one in-app browser session instead of the full Safari app.
      launchNativeGoogleBroker();
      return;
    }
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const handleFederated = useCallback(async (provider) => {
    setBusy(provider);
    setError(null);
    try {
      const nonce = generateNonce();

      // Microsoft uses full-page redirect (loginRedirect). Execution
      // never returns from this branch on success — the browser
      // navigates to Entra and lands back at <MicrosoftAuthCallback>.
      if (provider === "microsoft") {
        try {
          await initiateMicrosoftRedirect({ nonce });
          // Web: the page is navigating away. Native: the system browser is
          // up and this screen stays mounted — release the button so a
          // cancelled attempt can be retried.
          setBusy(null);
          return;
        } catch (err) {
          setBusy(null);
          if (err instanceof FederatedAuthError) {
            setError({ title: "Identity provider error", message: err.message, code: err.code });
          } else {
            setError(describeSsoBackendError(err));
          }
          return;
        }
      }

      // Apple continues to use its popup id_token flow. Apple returns the
      // user's name only on the FIRST authorization — forward it so a new
      // account is not provisioned as "Traveler".
      const idpResult = await signInWithApple({ nonce });
      if (!idpResult.native) {
        // Web only. Native already holds the bearer pair from the mobile exchange.
        await api.post(
          `/api/auth/sso/${provider}/signin`,
          { provider_token: idpResult.id_token, nonce, display_name: appleDisplayName(idpResult.user) },
        );
      }
      const me = await onSignedIn?.();
      if (idpResult.native && me === null) {
        // Bearer pair stored but /api/auth/me did not hydrate (refreshMe
        // swallows 401s and returns null). Never leave Login silent.
        setError(nativeSessionNotEstablishedError("apple"));
      }
    } catch (err) {
      if (err instanceof FederatedAuthError) {
        if (err.code === "apple_not_completed") {
          setError({ title: "Apple sign-in did not complete", message: err.message, code: err.code });
          return;
        }
        if (err.code === "apple_session_not_established") {
          setError({ title: "Session could not be saved", message: err.message, code: err.code });
          return;
        }
        if (err.code === "apple_popup_closed" || err.code === "microsoft_popup_closed") {
          setError({ title: "Sign-in cancelled", message: "You closed the sign-in window before finishing.", code: err.code });
        } else {
          setError({ title: "Identity provider error", message: err.message, code: err.code });
        }
      } else {
        setError(describeSsoBackendError(err));
      }
    } finally {
      if (provider !== "microsoft") setBusy(null);
    }
  }, [onSignedIn]);

  return (
    <>
      {/* Header */}
      <div className="mb-9">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.07] border border-white/15 px-2.5 py-1 mb-4">
          <ShieldCheck className="w-3 h-3 text-[#ffd66b]" strokeWidth={2.4} />
          <span className="text-[10px] font-black tracking-[0.24em] uppercase text-white/80">
            Secure sign-in
          </span>
        </div>
        <h2 className="font-display font-bold text-white text-[26px] leading-[1.2] tracking-normal" data-testid="cc-auth-sign-in-heading">
          SIGN IN OR CREATE YOUR ACCOUNT
        </h2>
        <p className="mt-2 text-[14.5px] text-white/60 leading-relaxed">
          Sign in with Google, Apple, or Microsoft to access Cruise Command.
        </p>
      </div>

      {/* Providers */}
      <div className="space-y-3" data-testid="cc-auth-providers">
        <ProviderButton
          testId="cc-auth-google"
          glyph={<GoogleGlyph className="w-[18px] h-[18px]" />}
          label="Continue with Google"
          onClick={handleGoogle}
          busy={busy === "google"}
          disabled={busy !== null}
        />
        {/* Apple sign-in restored 2026-10-03 (hidden 2026-08-20 pending E2E). */}
        <ProviderButton
          testId="cc-auth-apple"
          glyph={<AppleGlyph className="w-[18px] h-[18px]" />}
          label="Continue with Apple"
          onClick={() => handleFederated("apple")}
          busy={busy === "apple"}
          disabled={busy !== null}
        />
        <ProviderButton
          testId="cc-auth-microsoft"
          glyph={<MicrosoftGlyph className="w-[16px] h-[16px]" />}
          label="Continue with Microsoft"
          onClick={() => handleFederated("microsoft")}
          busy={busy === "microsoft"}
          disabled={busy !== null}
        />
      </div>

      {/* Error surface */}
      {error && (
        <div
          data-testid="cc-auth-sso-error"
          data-error-code={error.code || "unknown"}
          className="mt-5 rounded-2xl bg-red-500/[0.08] border border-red-500/25 px-5 py-4"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" strokeWidth={2.4} />
            <div className="min-w-0">
              <div className="text-[12.5px] font-bold text-red-200">{error.title}</div>
              <p className="mt-1 text-[12.5px] text-red-100/80 leading-relaxed">{error.message}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// -----------------------------------------------------------------
// ProviderButton — unified white pill with glyph, label, and busy state.
// Every provider is fully operational; there is no disabled placeholder.
// -----------------------------------------------------------------
function ProviderButton({ testId, glyph, label, onClick, busy, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-busy={busy ? "true" : "false"}
      disabled={disabled}
      className={
        "group w-full rounded-2xl h-[52px] text-[14.5px] font-semibold text-slate-900 bg-white inline-flex items-center justify-between px-5 transition-all " +
        (disabled && !busy
          ? "opacity-70 cursor-wait"
          : "hover:-translate-y-0.5 hover:shadow-[0_14px_32px_rgba(0,0,0,0.28)] shadow-[0_8px_22px_rgba(0,0,0,0.16)]")
      }
    >
      <span className="inline-flex items-center gap-3">
        <span>{glyph}</span>
        <span>{label}</span>
      </span>
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
      ) : (
        <ArrowRight className="w-4 h-4 text-slate-500 group-hover:translate-x-0.5 transition-transform" />
      )}
    </button>
  );
}

// -----------------------------------------------------------------
// Provider glyphs
// -----------------------------------------------------------------
function GoogleGlyph({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.6 2.3 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.5 17.7 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.6c0-1.7-.2-3.4-.5-5H24v9.5h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.6z"/>
      <path fill="#FBBC05" d="M10.5 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6L2.6 13.3C.9 16.6 0 20.2 0 24s.9 7.4 2.6 10.7l7.9-6.1z"/>
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.2 15.9-5.8l-7.6-5.9c-2.1 1.4-4.8 2.2-8.3 2.2-6.3 0-11.6-4-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

function AppleGlyph({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#111" d="M17.564 12.63c-.02-2.213 1.812-3.281 1.895-3.334-1.034-1.51-2.64-1.717-3.213-1.742-1.367-.138-2.669.807-3.365.807-.696 0-1.767-.786-2.907-.764-1.495.022-2.874.87-3.643 2.207-1.552 2.69-.398 6.673 1.115 8.855.741 1.07 1.622 2.27 2.775 2.226 1.117-.046 1.539-.72 2.887-.72 1.347 0 1.727.72 2.907.694 1.2-.02 1.96-1.09 2.694-2.163.849-1.24 1.198-2.442 1.219-2.505-.027-.011-2.34-.898-2.364-3.561zm-2.24-6.539c.615-.746 1.03-1.782.917-2.815-.886.035-1.96.591-2.596 1.337-.57.66-1.07 1.716-.936 2.73.988.076 1.999-.503 2.615-1.252z" />
    </svg>
  );
}

function MicrosoftGlyph({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="1"  y="1"  width="10" height="10" fill="#F25022" />
      <rect x="13" y="1"  width="10" height="10" fill="#7FBA00" />
      <rect x="1"  y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

// -----------------------------------------------------------------
// Verification screens (email verify · retained)
// -----------------------------------------------------------------
function VerifyPending({ email, onBackToLogin }) {
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState(null);
  const resend = async () => {
    if (!email) return;
    setSending(true);
    try {
      await api.post("/api/auth/resend-verification", { email });
      setSentAt(Date.now());
    } finally { setSending(false); }
  };
  return (
    <div className="text-center">
      <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#ffd66b]/15 border border-[#ffd66b]/30 flex items-center justify-center">
        <MailCheck className="w-8 h-8 text-[#ffd66b]" strokeWidth={2} />
      </div>
      <h2 className="font-display font-bold text-white text-[28px] leading-tight">Check your email.</h2>
      <p className="mt-3 text-[14px] text-white/70 leading-relaxed max-w-sm mx-auto">
        {email ? <>We sent a secure link to <span className="text-white font-semibold">{email}</span>.</> : "We sent a secure link to your inbox."}
      </p>
      <div className="mt-6 space-y-3">
        <button type="button" onClick={resend} disabled={sending || !email}
                data-testid="cc-auth-resend-verification"
                className="w-full rounded-full h-11 text-[13px] font-semibold text-white border border-white/25 hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : sentAt ? "Reissued ✓" : "Reissue verification link"}
        </button>
        <button type="button" onClick={onBackToLogin}
                data-testid="cc-auth-verify-pending-back"
                className="text-[12.5px] text-white/60 hover:text-white/90">
          Back to sign in
        </button>
      </div>
    </div>
  );
}

function VerifyConsuming() {
  return (
    <div className="text-center">
      <Loader2 className="w-8 h-8 text-white/80 animate-spin mx-auto mb-6" />
      <p className="text-[14px] text-white/70">Verifying your email…</p>
    </div>
  );
}

function VerifySuccess({ onContinue }) {
  return (
    <div className="text-center">
      <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-emerald-400" strokeWidth={2} />
      </div>
      <h2 className="font-display font-bold text-white text-[28px] leading-tight">You&apos;re all set.</h2>
      <p className="mt-3 text-[14px] text-white/70 leading-relaxed max-w-sm mx-auto">
        Your email is verified. Sign in with your identity provider to open your voyage.
      </p>
      <button type="button" onClick={onContinue}
              data-testid="cc-auth-verify-success-continue"
              className="mt-8 rounded-full px-6 h-12 text-[14px] font-bold text-[#1a1204] inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
              style={{ background: "linear-gradient(135deg, #ffd66b 0%, #f5b400 100%)", boxShadow: "0 10px 26px rgba(245,180,0,0.35)" }}>
        Continue to sign in <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function VerifyError({ message, onBack }) {
  return (
    <div className="text-center">
      <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center">
        <Lock className="w-8 h-8 text-red-400" strokeWidth={2} />
      </div>
      <h2 className="font-display font-bold text-white text-[28px] leading-tight">Verification failed.</h2>
      <p className="mt-3 text-[13.5px] text-white/70 leading-relaxed max-w-sm mx-auto">
        {message || "The link is invalid or expired. Please contact your voyage administrator for a fresh invitation."}
      </p>
      <button type="button" onClick={onBack}
              className="mt-8 rounded-full px-6 h-12 text-[13.5px] font-semibold text-white border border-white/25 hover:bg-white/10 inline-flex items-center gap-2">
        Back to sign in
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// Retired legacy magic-link URL (?invite=<token>)
// -----------------------------------------------------------------
function InviteRetired({ onBack }) {
  return (
    <div className="text-center">
      <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
        <Lock className="w-8 h-8 text-amber-300" strokeWidth={2} />
      </div>
      <h2 className="font-display font-bold text-white text-[28px] leading-tight">Invitation method retired.</h2>
      <p className="mt-3 text-[13.5px] text-white/70 leading-relaxed max-w-sm mx-auto">
        This invitation method is no longer available. Sign in normally to continue to Cruise Command.
      </p>
      <button type="button" onClick={onBack} data-testid="cc-auth-invite-retired-sign-in"
              className="mt-8 rounded-full px-6 h-12 text-[13.5px] font-semibold text-white border border-white/25 hover:bg-white/10 inline-flex items-center gap-2">
        Go to normal sign in
      </button>
    </div>
  );
}
